import path from 'path';

import Hls from 'hls.js';
import fetch from 'isomorphic-fetch';
import _map from 'lodash/map';

import { HEADER_ACCEPT } from 'constants/http';
import { M3U8, PLAIN_TEXT } from 'constants/mimes';

import { M3u8Adapter } from './m3u8adapter';

// This takes all the key/value pairs of the partCredentials mapping and adds
// them as URL params to each part URL in the playlist file. Example:
//
// partCredentials: { 'Key-Pair-Id': 'ABCD', 'Expires': 100000, 'Token': 'EFGH' }
// output: '?Key-Pair-Id=ABCD&Expires=100000&Token=EFGH'
//
// Now 'output' can be appended to each part file in the playlist like so:
//
// cloudfront.net/sessions/raw/<video_id><output>
function getCredentialParamsString(partCredentials) {
  return `${_map(Object.keys(partCredentials), paramKey => {
    return `${paramKey}=${window.encodeURIComponent(
      partCredentials[paramKey]
    )}`;
  }).join('&')}`;
}

export const createHlsInstance = (playlistUrl, videoEl, hlsConfig) => {
  const hls = new Hls(hlsConfig);

  hls.loadSource(playlistUrl);
  hls.attachMedia(videoEl);

  return hls;
};

/**
 * @param {string | URL} playlistUrl
 */
const getCdnPrefix = playlistUrl => {
  const url = new URL(playlistUrl);

  return `${url.protocol}//${url.host}`;
};

/**
 * @param {string} playlistUrl
 *
 * @return {string}
 */
export const getAbsolutePath = playlistUrl => {
  const a = document.createElement('a');

  a.href = playlistUrl;

  const cdnPrefix = getCdnPrefix(playlistUrl);
  const parentPath = path.dirname(a.pathname);

  // Prepend each URL with the path on CloudFront. By default, these part
  // URLs are relative, which works when we're referencing the playlist
  // directly off of CloudFront, but we're actually referencing
  // blob://loom.com in this case.
  return cdnPrefix + parentPath;
};

/**
 * We take the URL of the m3u8 and do the following:
 * - convert every URL to an absolute URL
 * - append the CF signature to every single URL
 * We do this iteratively with every playlist referenced in the master m3u8,
 * so in the end all the URIs have the CF signatures appended
 *
 * The reason to do this and not have the player do it:
 * hls.js can do this, but hls.js does not support iOS, so we have do it anyway for
 * iOS (we couldn't find a better way)
 *
 *  @returns A single string with base64 encoded playlists
 */
export const getHlsPlaylistWithCredentials = (playlistUrl, partCredentials) => {
  const credentialParamsString = getCredentialParamsString(partCredentials);

  return fetch(playlistUrl, {
    headers: {
      [HEADER_ACCEPT]: PLAIN_TEXT,
    },
  })
    .then(resp => resp.text())
    .then(async resp => {
      const absolutePath = getAbsolutePath(playlistUrl);

      const finalPlaylist = M3u8Adapter.changeUrls(
        resp,
        absolutePath,
        credentialParamsString
      );

      const processor = async url => {
        try {
          return await getHlsPlaylistWithCredentials(url, partCredentials);
        } catch (err) {
          // do not error if some playlists are missing
          return '';
        }
      };

      const processedPlaylist = await M3u8Adapter.processPlaylists(
        finalPlaylist,
        processor
      );

      // Create a data string that will be used as the playlist directly by hls.js
      // or by the video element if it supports native HLS
      //
      // NOTE: we must use a data string instead of a blob URL since, if we're
      //       playing natively in iOS Safari, it can't guess the data type from a
      //       blob URL
      return `data:${M3U8};base64,${window.btoa(processedPlaylist)}`;
    });
};
